import { el } from '../util/dom';
import { DailyUsageSummary, WeeklyUsageData, BlockStats, UsageTrend, DailyLimitPrediction, formatDuration } from './types';

// Create a pie chart for today's app usage (using SVG-based pie chart)
export function createDailyUsagePieChart(usage: DailyUsageSummary): HTMLElement {
  const container = el('div', { class: 'usage-pie-container' });
  const title = el('h3', { class: 'chart-title' }, 'Today\'s App Usage');
  container.appendChild(title);

  if (usage.apps.length === 0) {
    const empty = el('div', { class: 'chart-empty' }, 'No usage data for today');
    container.appendChild(empty);
    return container;
  }

  // Create legend
  const legend = el('div', { class: 'pie-legend' });
  const colors = generateColors(usage.apps.length);

  usage.apps.forEach((app, idx) => {
    const item = el('div', { class: 'pie-legend-item' });
    const color = el('div', {
      class: 'pie-legend-color',
      style: `background: ${colors[idx]};`,
    });
    const label = el('div', { class: 'pie-legend-label' });
    const name = el('div', { class: 'pie-legend-name' }, app.appName || app.packageName);
    const duration = el('div', { class: 'pie-legend-duration' }, formatDuration(app.duration));

    label.appendChild(name);
    label.appendChild(duration);
    item.appendChild(color);
    item.appendChild(label);
    legend.appendChild(item);
  });

  container.appendChild(legend);

  // Create simple SVG pie chart
  const svg = createPieChartSVG(usage.apps, colors, 150);
  container.appendChild(svg);

  return container;
}

// Create a bar chart for weekly usage
export function createWeeklyUsageBarChart(week: WeeklyUsageData): HTMLElement {
  const container = el('div', { class: 'usage-bar-container' });
  const title = el('h3', { class: 'chart-title' }, 'Weekly Screen Time');
  container.appendChild(title);

  // Create bars
  const bars = el('div', { class: 'weekly-bars' });

  const maxUsage = Math.max(...week.daily.map(d => d.totalScreenTime), 1);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  week.daily.forEach((day, idx) => {
    const barItem = el('div', { class: 'bar-item' });
    const barLabel = el('div', { class: 'bar-label' }, dayNames[day.date.getDay()]);
    const barTrack = el('div', { class: 'bar-track' });
    const barFill = el('div', {
      class: 'bar-fill',
      style: `height: ${(day.totalScreenTime / maxUsage) * 100}%;`,
      title: formatDuration(day.totalScreenTime),
    });
    const barValue = el('div', { class: 'bar-value' }, formatDuration(day.totalScreenTime));

    barTrack.appendChild(barFill);
    barItem.appendChild(barLabel);
    barItem.appendChild(barTrack);
    barItem.appendChild(barValue);
    bars.appendChild(barItem);
  });

  container.appendChild(bars);

  // Summary stats
  const stats = el('div', { class: 'weekly-stats' });
  const totalStat = el('div', { class: 'stat-item' });
  totalStat.appendChild(el('span', { class: 'stat-label' }, 'Total:'));
  totalStat.appendChild(el('span', { class: 'stat-value' }, formatDuration(week.total)));
  stats.appendChild(totalStat);

  const avgStat = el('div', { class: 'stat-item' });
  avgStat.appendChild(el('span', { class: 'stat-label' }, 'Average:'));
  avgStat.appendChild(el('span', { class: 'stat-value' }, formatDuration(week.averageDaily)));
  stats.appendChild(avgStat);

  container.appendChild(stats);

  return container;
}

// Create block events summary
export function createBlockEventsSummary(stats: BlockStats): HTMLElement {
  const container = el('div', { class: 'block-events-container' });
  const title = el('h3', { class: 'chart-title' }, 'Block Events');
  container.appendChild(title);

  // Summary stats
  const summary = el('div', { class: 'block-summary' });

  const totalCard = el('div', { class: 'block-stat-card' });
  totalCard.appendChild(el('div', { class: 'block-stat-label' }, 'Total Blocks'));
  totalCard.appendChild(el('div', { class: 'block-stat-value' }, String(stats.totalBlocks)));
  summary.appendChild(totalCard);

  const scheduleCard = el('div', { class: 'block-stat-card' });
  scheduleCard.appendChild(el('div', { class: 'block-stat-label' }, 'Schedule'));
  scheduleCard.appendChild(el('div', { class: 'block-stat-value' }, String(stats.byReason.schedule)));
  summary.appendChild(scheduleCard);

  const limitCard = el('div', { class: 'block-stat-card' });
  limitCard.appendChild(el('div', { class: 'block-stat-label' }, 'Limit Reached'));
  limitCard.appendChild(el('div', { class: 'block-stat-value' }, String(stats.byReason.limit_reached)));
  summary.appendChild(limitCard);

  const blocklistCard = el('div', { class: 'block-stat-card' });
  blocklistCard.appendChild(el('div', { class: 'block-stat-label' }, 'Blocklist'));
  blocklistCard.appendChild(el('div', { class: 'block-stat-value' }, String(stats.byReason.blocklist)));
  summary.appendChild(blocklistCard);

  container.appendChild(summary);

  // Top blocked apps
  if (Object.keys(stats.byApp).length > 0) {
    const topApps = el('div', { class: 'top-blocked-apps' });
    const topTitle = el('div', { class: 'block-subsection-title' }, 'Top Blocked Apps');
    topApps.appendChild(topTitle);

    const sortedApps = Object.entries(stats.byApp)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    sortedApps.forEach(([appName, count]) => {
      const row = el('div', { class: 'blocked-app-row' });
      row.appendChild(el('span', { class: 'app-name' }, appName));
      row.appendChild(el('span', { class: 'block-count' }, `${count}x`));
      topApps.appendChild(row);
    });

    container.appendChild(topApps);
  }

  return container;
}

// Create usage trends visualization
export function createUsageTrendsView(trends: UsageTrend[]): HTMLElement {
  const container = el('div', { class: 'trends-container' });
  const title = el('h3', { class: 'chart-title' }, 'Usage Trends');
  container.appendChild(title);

  if (trends.length === 0) {
    const empty = el('div', { class: 'chart-empty' }, 'No trend data available');
    container.appendChild(empty);
    return container;
  }

  const list = el('div', { class: 'trends-list' });

  trends.forEach(trend => {
    const item = el('div', { class: `trend-item trend-${trend.trend}` });

    const header = el('div', { class: 'trend-header' });
    header.appendChild(el('div', { class: 'trend-app-name' }, trend.appName));
    const arrow = el('span', { class: 'trend-arrow' },
      trend.trend === 'up' ? '↑' : trend.trend === 'down' ? '↓' : '→');
    header.appendChild(arrow);
    item.appendChild(header);

    const stats = el('div', { class: 'trend-stats' });
    stats.appendChild(el('span', { class: 'trend-prev' }, `${formatDuration(trend.previous)} yesterday`));
    stats.appendChild(el('span', { class: 'trend-current' }, `${formatDuration(trend.current)} today`));
    stats.appendChild(el('span', { class: 'trend-change' }, `${trend.change > 0 ? '+' : ''}${Math.round(trend.change)}%`));
    item.appendChild(stats);

    list.appendChild(item);
  });

  container.appendChild(list);
  return container;
}

// Create daily limit prediction widget
export function createDailyLimitPrediction(prediction: DailyLimitPrediction): HTMLElement {
  const container = el('div', { class: 'limit-prediction-container' });
  const title = el('h3', { class: 'chart-title' }, 'Daily Limit Status');
  container.appendChild(title);

  const card = el('div', { class: `limit-card ${prediction.willExceed ? 'will-exceed' : 'ok'}` });

  // Progress bar
  const progress = el('div', { class: 'limit-progress' });
  const bar = el('div', { class: 'progress-bar' });
  const fill = el('div', {
    class: 'progress-fill',
    style: `width: ${Math.min(prediction.percentUsed, 100)}%;`,
  });
  bar.appendChild(fill);
  progress.appendChild(bar);
  progress.appendChild(el('div', { class: 'progress-label' }, `${Math.round(prediction.percentUsed)}%`));
  card.appendChild(progress);

  // Stats
  const stats = el('div', { class: 'limit-stats' });

  const usedStat = el('div', { class: 'limit-stat-row' });
  usedStat.appendChild(el('span', { class: 'limit-stat-label' }, 'Used today:'));
  usedStat.appendChild(el('span', { class: 'limit-stat-value' }, formatDuration(prediction.currentUsage)));
  stats.appendChild(usedStat);

  const limitStat = el('div', { class: 'limit-stat-row' });
  limitStat.appendChild(el('span', { class: 'limit-stat-label' }, 'Daily limit:'));
  limitStat.appendChild(el('span', { class: 'limit-stat-value' }, formatDuration(prediction.dailyLimit)));
  stats.appendChild(limitStat);

  const remainingStat = el('div', { class: 'limit-stat-row' });
  remainingStat.appendChild(el('span', { class: 'limit-stat-label' }, 'Remaining:'));
  const remainingValue = Math.max(0, prediction.dailyLimit - prediction.currentUsage);
  remainingStat.appendChild(el('span', { class: 'limit-stat-value' }, formatDuration(remainingValue)));
  stats.appendChild(remainingStat);

  if (prediction.willExceed && prediction.estimatedTimeUntilLimit) {
    const exceedStat = el('div', { class: 'limit-stat-row warning' });
    exceedStat.appendChild(el('span', { class: 'limit-stat-label' }, '⚠️ Limit exceeded at:'));
    const timeStr = prediction.estimatedTimeUntilLimit.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    exceedStat.appendChild(el('span', { class: 'limit-stat-value' }, timeStr));
    stats.appendChild(exceedStat);
  }

  card.appendChild(stats);
  container.appendChild(card);

  return container;
}

// Helper: Generate colors for pie chart
function generateColors(count: number): string[] {
  const colors = [
    '#3b82f6',
    '#ef4444',
    '#10b981',
    '#f59e0b',
    '#8b5cf6',
    '#ec4899',
    '#06b6d4',
    '#84cc16',
    '#f97316',
    '#6366f1',
  ];
  const result: string[] = [];
  for (let i = 0; i < count; i++) {
    result.push(colors[i % colors.length]);
  }
  return result;
}

// Helper: Create SVG pie chart
function createPieChartSVG(apps: any[], colors: string[], radius: number): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'pie-chart');
  svg.setAttribute('viewBox', `0 0 ${radius * 2} ${radius * 2}`);
  svg.setAttribute('width', String(radius * 2));
  svg.setAttribute('height', String(radius * 2));

  const total = apps.reduce((sum, app) => sum + app.duration, 0);
  if (total === 0) return svg;

  let currentAngle = -90;
  const cx = radius;
  const cy = radius;

  apps.forEach((app, idx) => {
    const sliceAngle = (app.duration / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sliceAngle;

    const path = createPieSlice(cx, cy, radius, startAngle, endAngle);
    const slice = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    slice.setAttribute('d', path);
    slice.setAttribute('fill', colors[idx]);
    slice.setAttribute('stroke', 'white');
    slice.setAttribute('stroke-width', '2');
    svg.appendChild(slice);

    currentAngle = endAngle;
  });

  return svg;
}

// Helper: Create SVG pie slice path
function createPieSlice(cx: number, cy: number, radius: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
}

// Helper: Convert polar to Cartesian coordinates
function polarToCartesian(cx: number, cy: number, radius: number, angleInDegrees: number): { x: number; y: number } {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
}
